import { z } from 'zod';

import { themeButtonSchema, themeColorSchema, themeSelectedFontsSchema, themeSelectionSchema } from '../base';

/**
 * What a website theme actually holds — the three selections and the untyped root bag.
 *
 * Split out from the entity wrapper so the payload has a name of its own: this is the object the
 * editor's state, its settings panels and the SSR renderer all work on, and the entity tag around it
 * is bookkeeping they never touch.
 *
 * **Why every part is optional.** A page saved before the user opened any settings panel carries only
 * what its template seeded, and pages in the wild have been observed with any subset. Requiring all
 * three would fail the parse for a page that renders perfectly today — and a theme that fails to
 * parse is a site that loses its colours.
 *
 * **No `root`.** The legacy blob has one, and the reader that cared — `apps/websites-builder`
 * pulling `root.data.backgroundVideoFile` for a background — is a MINI-LANDING path. That variant
 * lives in `@libs/themes` and is not versioned, so nothing on the website side ever reads `root`;
 * declaring it here would have been carrying another variant's field through a schema that cannot
 * describe it. The legacy schema still accepts it, so a stored theme carrying one parses.
 */
export const websiteThemePropsSchema = z.object({
    colors: themeSelectionSchema(themeColorSchema).optional(),
    fonts: themeSelectedFontsSchema.optional(),
    buttons: themeSelectionSchema(themeButtonSchema).optional(),
});

/**
 * The website theme, v0 — what `draft_theme_settings` holds for a site built with the website
 * variant.
 *
 * `props` rather than the fields inline, matching every other entity in this workspace: the tag and
 * the version sit at the top, the payload sits under `props`. A reader can then tell "which entity
 * and which version" from "what it holds" without knowing either.
 */
export const websiteThemeV0Schema = z.object({
    entityType: z.literal('theme'),
    entitySubType: z.literal('website'),
    version: z.literal(0),
    props: websiteThemePropsSchema,
});

export type WebsiteThemeProps = z.infer<typeof websiteThemePropsSchema>;
export type WebsiteThemeV0 = z.infer<typeof websiteThemeV0Schema>;
